import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const BenefitNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #0d9488;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  margin-right: 1.5rem;
  flex-shrink: 0;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const BenefitDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const InsuranceOptions = styled.div`
  margin: 5rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const OptionIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const OptionTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const OptionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Insurance: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const insuranceBenefits = [
    {
      number: 1,
      title: language === 'it' ? 'COPERTURA PER DANNI E FURTI' : 'DAMAGE AND THEFT COVERAGE',
      description: language === 'it' 
        ? 'L\'assicurazione copre i danni alla proprietà causati da eventi come incendio, alluvione, uragano o furto. Questa copertura può estendersi anche ai mobili e ad altri beni presenti nella proprietà.'
        : 'The insurance covers damage to property caused by events such as fire, flood, hurricane or theft. This coverage can also extend to furniture and other property in the property.'
    },
    {
      number: 2,
      title: language === 'it' ? 'RESPONSABILITÀ CIVILE' : 'LIABILITY',
      description: language === 'it' 
        ? 'Fornisce copertura per eventuali lesioni fisiche o danni alla proprietà causati agli ospiti o a terzi durante il loro soggiorno presso la proprietà. La responsabilità civile è particolarmente importante nei contesti di affitto a breve termine dove gli ospiti possono variare frequentemente.'
        : 'Provides coverage for any physical injury or property damage caused to guests or third parties during their stay at the property. Civil liability is particularly important in short-term rental contexts where guests may vary frequently.'
    },
    {
      number: 3,
      title: language === 'it' ? 'PERDITA DI REDDITO' : 'LOSS OF INCOME',
      description: language === 'it' 
        ? 'Se ci sono danni alla tua proprietà che la rendono temporaneamente inabitabile e impossibile da affittare, l\'assicurazione può coprire la perdita di reddito derivante dall\'affitto perso durante le riparazioni.'
        : 'If there is damage to your property that makes it temporarily uninhabitable and impossible to rent, insurance may cover loss of income resulting from rent lost during repairs.'
    },
    {
      number: 4,
      title: language === 'it' ? 'ASSISTENZA LEGALE' : 'LEGAL ASSISTANCE',
      description: language === 'it' 
        ? 'Può fornire assistenza legale e coprire i costi legali in caso di controversie o azioni legali relative alla proprietà da parte di inquilini o terzi.'
        : 'Can provide legal assistance and cover legal costs in the event of disputes or legal actions relating to the property by tenants or third parties.'
    },
    {
      number: 5,
      title: language === 'it' ? 'RESPONSABILITÀ DEL PROPRIETARIO' : 'OWNER\'S LIABILITY',
      description: language === 'it' 
        ? 'Copre il proprietario in caso di danni causati da difetti strutturali o manutenzione insufficiente della proprietà, fornendo una sicurezza aggiuntiva.'
        : 'Covers the owner in case of damage caused by structural defects or insufficient maintenance of the property, providing additional security.'
    },
    {
      number: 6,
      title: language === 'it' ? 'ASSICURAZIONE CONTRO INQUILINI NON PAGANTI' : 'NON-PAYING TENANT INSURANCE',
      description: language === 'it' 
        ? 'Alcune polizze includono la copertura contro inquilini che non pagano l\'affitto o che causano danni alla proprietà.'
        : 'Some policies include coverage against tenants who fail to pay rent or who cause damage to the property.'
    },
    {
      number: 7,
      title: language === 'it' ? 'PERIODI DI INATTIVITÀ' : 'PERIODS OF DOWNTIME',
      description: language === 'it' 
        ? 'Se la proprietà rimane vacante per un periodo prolungato tra un affitto e l\'altro, alcune polizze possono offrire copertura anche durante questi periodi.'
        : 'If the property remains vacant for an extended period between rentals, some policies may offer cover during these periods as well.'
    },
    {
      number: 8,
      title: language === 'it' ? 'PERSONALIZZAZIONE DELLE POLIZZE' : 'CUSTOMIZATION OF POLICIES',
      description: language === 'it' 
        ? 'Le polizze per affitti a breve termine possono essere personalizzate in base alle esigenze specifiche della proprietà e dell\'attività di locazione.'
        : 'Short-term rental policies can be customized based on the specific needs of the property and rental business.'
    }
  ];
  
  const insuranceOptions = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        </svg>
      ),
      title: language === 'it' ? 'ASSICURAZIONE BASE' : 'BASIC INSURANCE',
      description: language === 'it' 
        ? 'Copertura essenziale per danni alla proprietà, responsabilità civile e alcune perdite di reddito, ideale per proprietari che iniziano con gli affitti brevi.'
        : 'Essential coverage for property damage, liability and some income loss, ideal for owners starting with short-term rentals.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="9" y1="21" x2="9" y2="9"></line>
        </svg>
      ),
      title: language === 'it' ? 'ASSICURAZIONE PREMIUM' : 'PREMIUM INSURANCE',
      description: language === 'it' 
        ? 'Protezione completa con coperture estese, assistenza legale avanzata e protezione contro periodi di inattività, per proprietari con più immobili.'
        : 'Complete protection with extended coverage, advanced legal assistance and protection against periods of inactivity, for owners with multiple properties.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      title: language === 'it' ? 'CONSULENZA PERSONALIZZATA' : 'PERSONALIZED CONSULTATION',
      description: language === 'it' 
        ? 'Analisi dettagliata delle tue esigenze specifiche per creare un pacchetto assicurativo su misura per la tua proprietà e la tua attività di affitti brevi.'
        : 'Detailed analysis of your specific needs to create a tailored insurance package for your property and short-term rental business.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Assicurazione | J-O-H' : 'Insurance | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Soluzioni assicurative su misura per la protezione degli immobili in affitto breve e della tua attività di hosting' 
            : 'Tailored insurance solutions for the protection of short-term rental properties and your hosting business'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'ASSICURAZIONE' : 'INSURANCE'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'L\'assicurazione per immobili in affitto a breve termine è una forma di copertura assicurativa progettata specificamente per proteggere i proprietari di case vacanze, appartamenti o proprietà destinate ad affitti temporanei. Questo tipo di assicurazione offre una serie di vantaggi unici per adattarsi alle sfide specifiche associate agli affitti a breve termine.'
              : 'Short-term rental property insurance is a form of insurance coverage designed specifically to protect owners of vacation homes, apartments or properties intended for temporary rentals. This type of insurance offers a number of unique benefits to adapt to the specific challenges associated with short-term rentals.'}
          </Description>
          
          <BenefitsGrid>
            {insuranceBenefits.map((benefit) => (
              <BenefitCard key={benefit.number}>
                <BenefitNumber>{benefit.number}</BenefitNumber>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitContent>
              </BenefitCard>
            ))}
          </BenefitsGrid>
          
          <SectionTitle>
            {language === 'it' ? 'LE NOSTRE OPZIONI ASSICURATIVE' : 'OUR INSURANCE OPTIONS'}
          </SectionTitle>
          
          <InsuranceOptions>
            {insuranceOptions.map((option, index) => (
              <OptionCard key={index}>
                <OptionIcon>
                  {option.icon}
                </OptionIcon>
                <OptionTitle>{option.title}</OptionTitle>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionCard>
            ))}
          </InsuranceOptions>
          
          <Description>
            {language === 'it' 
              ? 'È essenziale considerare l\'assicurazione come parte integrante della gestione della proprietà. Una copertura adeguata può offrire tranquillità e protezione finanziaria contro una serie di rischi associati agli affitti a breve termine.'
              : 'It is essential to carefully consider insurance as an integral part of property management. Proper cover can offer peace of mind and financial protection against a range of risks associated with short-term rentals.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Insurance; 